/**
 * /development/collections — the Collections hub. Lists the collections the
 * caller owns or has been shared (grant-scoped server-side) using the shared
 * Projects card pattern, and links to create a new one.
 *
 * "Collection" is the UI name for a document index; the API is /api/v1/indexes.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconBooks, IconPlus } from "@tabler/icons-react";
import { Link as RouterLink } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { createdOn } from "@/features/projects/creatorMeta";
import { listIndexes, type IndexOut } from "@/shared/api/indexes";
import { listCardActionStyle, listCardStyle } from "@/shared/ui/listCard";
import { collectionStatusColor, ownerLabel } from "./collectionMeta";

export function CollectionsList() {
  const getToken = useAccessToken();
  const [items, setItems] = useState<IndexOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const page = await listIndexes(token);
      setItems(page.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>Collections</Title>
        <Button
          component={RouterLink}
          to="/development/collections/new"
          leftSection={<IconPlus size={16} />}
        >
          New collection
        </Button>
      </Group>
      <Text c="dimmed" size="sm">
        A collection is a named set of documents that Explore, Create, Review and Quality work from.
        Collections are private until you share them.
      </Text>

      {error && (
        <Alert variant="light" color="red" title="Could not load collections">
          {error}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" p="xl">
          <Loader size="sm" />
        </Group>
      ) : items.length === 0 ? (
        <Paper withBorder radius="md" p="xl">
          <Text c="dimmed" ta="center">
            No collections yet. Create your first one.
          </Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" verticalSpacing="lg">
          {items.map((c) => (
            <Card
              key={c.index_id}
              withBorder
              radius="md"
              padding="lg"
              style={{
                ...listCardStyle,
                borderTop: "3px solid var(--mantine-primary-color-filled)",
              }}
            >
              <Group gap="sm" mb="xs" wrap="nowrap" justify="space-between">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <ThemeIcon variant="light" size={32} radius="md">
                    <IconBooks size={18} />
                  </ThemeIcon>
                  <Text fw={600} truncate>
                    {c.name}
                  </Text>
                </Group>
                <Badge variant="light" color={collectionStatusColor(c.status)}>
                  {c.status}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed" lineClamp={2}>
                {c.description ?? "No description"}
              </Text>
              <Text size="xs" mt="sm">
                {c.document_count} document{c.document_count === 1 ? "" : "s"} · {c.chunk_count}{" "}
                chunk{c.chunk_count === 1 ? "" : "s"} · last built{" "}
                {c.last_built_at_utc ? createdOn(c.last_built_at_utc) : "never"}
              </Text>
              <Text size="xs" c="dimmed" mt={4} lineClamp={2}>
                Created by {ownerLabel(c)} · {createdOn(c.created_at_utc)}
                {!c.is_owner && " · shared with you"}
              </Text>
              <Button
                mt="md"
                style={listCardActionStyle}
                variant="light"
                size="xs"
                component={RouterLink}
                to={`/development/collections/${c.index_id}`}
              >
                Open
              </Button>
            </Card>
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
